import { useState, useEffect, useCallback } from 'react';
import { storePurchaseOrdersAPI, storesAPI, vendorsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, RefreshCw, Eye, Building2, Truck, Package, 
  TrendingUp, DollarSign, FileText
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const FranchiseDirectPOMonitoring = () => {
  const { toast } = useToast();
  
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [storeFilter, setStoreFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [search, setSearch] = useState('');
  
  // Filter options
  const [stores, setStores] = useState([]);
  const [vendors, setVendors] = useState([]);
  
  // View PO
  const [selectedPO, setSelectedPO] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  const fetchPurchaseOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (storeFilter && storeFilter !== 'all') params.store_id = storeFilter;
      if (vendorFilter && vendorFilter !== 'all') params.vendor_id = vendorFilter;
      if (search) params.search = search;
      
      const response = await storePurchaseOrdersAPI.hqFranchiseDirectList(params);
      setPurchaseOrders(response.data.items || []);
    } catch (error) {
      console.error('Error fetching POs:', error);
      toast({ title: 'Error', description: 'Failed to fetch purchase orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, storeFilter, vendorFilter, search, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await storePurchaseOrdersAPI.hqFranchiseDirectStats();
      setStats(response.data || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const response = await storesAPI.list({ store_type: 'franchise', page_size: 100 });
      setStores(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const response = await vendorsAPI.list({ vendor_type: 'central', allow_franchise_direct_purchase: true });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  }, []);

  useEffect(() => {
    fetchPurchaseOrders();
    fetchStats();
    fetchStores();
    fetchVendors();
  }, [fetchPurchaseOrders, fetchStats, fetchStores, fetchVendors]);

  const getStatusBadge = (status) => {
    const config = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Processing' },
      dispatched: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Dispatched' },
      partially_received: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Partial' },
      completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-600', label: 'Cancelled' }
    };
    const c = config[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
    return <Badge className={`${c.bg} ${c.text}`}>{c.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="franchise-direct-po-monitoring">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-purple-600" />
            Franchise Direct PO Monitoring
          </h1>
          <p className="text-gray-500 mt-1">
            Monitor purchase orders placed by franchise stores directly to central vendors
          </p>
        </div>
        <Button onClick={fetchPurchaseOrders} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Total POs</p>
            <p className="text-2xl font-bold text-purple-600">
              {Object.values(stats.by_status || {}).reduce((sum, s) => sum + (s.count || 0), 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Total Value</p>
            <p className="text-xl font-bold text-green-600">
              {formatCurrency(Object.values(stats.by_status || {}).reduce((sum, s) => sum + (s.value || 0), 0))}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-blue-600">
              {stats.by_status?.completed?.count || 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Pending Delivery</p>
            <p className="text-2xl font-bold text-orange-600">
              {(stats.by_status?.approved?.count || 0) + (stats.by_status?.processing?.count || 0) + (stats.by_status?.dispatched?.count || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top Stores */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4 text-indigo-500" />
              Top Franchise Stores
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_stores && stats.top_stores.length > 0 ? (
              <div className="space-y-2">
                {stats.top_stores.slice(0, 5).map((store, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{store.store_name || 'Unknown'}</span>
                    <span className="font-medium">{formatCurrency(store.value)} ({store.count} POs)</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No data available</p>
            )}
          </CardContent>
        </Card>
        
        {/* Top Vendors */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Truck className="w-4 h-4 text-teal-500" />
              Top Central Vendors
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats.top_vendors && stats.top_vendors.length > 0 ? (
              <div className="space-y-2">
                {stats.top_vendors.slice(0, 5).map((vendor, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{vendor.vendor_name || 'Unknown'}</span>
                    <span className="font-medium">{formatCurrency(vendor.value)} ({vendor.count} POs)</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search PO number..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-[160px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Franchise Store" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Select value={vendorFilter} onValueChange={setVendorFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Central Vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  {vendors.map(vendor => (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PO Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>PO Number</TableHead>
                <TableHead>Franchise Store</TableHead>
                <TableHead>Central Vendor</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : purchaseOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No franchise direct POs found
                  </TableCell>
                </TableRow>
              ) : (
                purchaseOrders.map((po) => (
                  <TableRow key={po.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{po.po_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{po.store_name}</p>
                        <p className="text-xs text-gray-500">{po.store_code}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{po.vendor_name || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{po.vendor_code || ''}</p>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(po.order_date || po.created_at)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(po.grand_total)}
                    </TableCell>
                    <TableCell className="text-center">{getStatusBadge(po.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedPO(po);
                          setViewDialogOpen(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View PO Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              PO Details: {selectedPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPO && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Franchise Store</p>
                  <p className="font-medium">{selectedPO.store_name} ({selectedPO.store_code})</p>
                </div>
                <div>
                  <p className="text-gray-500">Central Vendor</p>
                  <p className="font-medium">{selectedPO.vendor_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Order Date</p>
                  <p className="font-medium">{formatDate(selectedPO.order_date || selectedPO.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  {getStatusBadge(selectedPO.status)}
                </div>
              </div>

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedPO.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.product_sku}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{item.ordered_qty}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right">{item.tax_rate}%</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(item.line_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal:</span>
                    <span>{formatCurrency(selectedPO.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tax:</span>
                    <span>{formatCurrency(selectedPO.tax_total)}</span>
                  </div>
                  <div className="flex justify-between font-bold pt-1 border-t">
                    <span>Grand Total:</span>
                    <span>{formatCurrency(selectedPO.grand_total)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedPO.notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-sm">{selectedPO.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchiseDirectPOMonitoring;
